/**
 * license-waste-brief.ts — Editorial copy layer for the License Waste headline.
 *
 * Composes the verdict, headline and lede for the License Waste card from the active
 * licensing posture (total monthly idle-seat spend + a top-3 list of idle
 * SKUs). The voice is "trusted advisor": short sentences, no em-dashes,
 * period-separated rather than dash-bridged. The verdict is capped at six
 * words, like every composer's.
 *
 * Contract: every editorial slot is ONE deterministic, data-driven line.
 * There is no variant table, no seed, no rotation — the same input always
 * produces the same brief, and all variation in what the user reads comes
 * from the data (severity, idle-seat count, top SKU, annualized figure),
 * never from picking among synonymous phrasings of the same fact.
 *
 * Voice anchor: "Chaos to clarity / strategically human." Lead with named
 * subjects (the noisy SKU, the unassigned-seat count) — never with a row
 * count or a metric grid label. When data is missing, surface what we have
 * and frame the gap; do not fabricate.
 *
 * Action-first fields (blueprint §6.2 row 9): `headline` names the reclaim
 * action and the unassigned-seat count; `lede` names the SKU carrying the
 * most idle spend (`topIdleSkus` is sorted by waste, descending), else the
 * unassigned-seat count. `headlineMonthly` stays as the tile figure.
 */

import type { ActionBriefFields } from './brief-shape'

/* -------------------------------------------------------------------------- */
/* Public shape                                                               */
/* -------------------------------------------------------------------------- */

export type LicenseWasteSeverity = 'clean' | 'low' | 'moderate' | 'large' | 'unknown'

export interface LicenseWasteIdleSku {
  /** Friendly SKU name, e.g. "Microsoft 365 E5". */
  displayName: string
  /** Number of paid-but-unassigned seats on this SKU. */
  idleSeats: number
  /** Estimated monthly $ waste for this SKU at list price. */
  estimatedMonthlyWasteUsd: number
}

export interface LicenseWasteBriefInput {
  /** Total monthly idle-seat cost in USD. null when catalog has no matches. */
  totalMonthlyWasteUsd?: number | null
  /** Annual figure for emphasis in the lead sentence. Optional — derive *12 if missing. */
  totalAnnualWasteUsd?: number | null
  /** Top idle SKUs by waste $ (server returns up to 3, descending). */
  topIdleSkus?: Array<LicenseWasteIdleSku>
  /** Unassigned seats on the priced, paid SKUs behind `totalMonthlyWasteUsd`, so the
   *  count and the dollar figure describe the same seats. Unpriced SKUs are left out:
   *  a free SKU missing from the free list (10,000-seat allocations) would otherwise
   *  swamp the count while adding nothing to the dollars. */
  totalIdleSeats?: number
  /** Unused by this composer — kept for call-site compatibility. */
  tenantId?: string
  /** Override "today" for tests. */
  asOfDate?: Date
}

export interface LicenseWasteBrief {
  /** ≤6-word verdict. Not drawn: LicenseUtilization reads the action-first fields and `headlineMonthly`. */
  verdict: string
  /** "$1,240/mo" headline number — formatted USD, falls back to "—" when unknown. */
  headlineMonthly: string
  /** Plain-text byline: "By Guardian. As of {time}." */
  byline: string
  /** One sentence under the headline. Lead with named subject. */
  leadSentence: string
  /** Severity bucket for color treatment in the consuming component. */
  severity: LicenseWasteSeverity
  /** Action-first h1 (blueprint §6.2 row 9). */
  headline: string
  /** Never set by this composer. */
  emphasis?: ActionBriefFields['emphasis']
  /** One sentence, one `**bold**` run naming the subject and its count. */
  lede: string
}

/* -------------------------------------------------------------------------- */
/* Internal helpers                                                           */
/* -------------------------------------------------------------------------- */

/** Format the "By Guardian. As of HH:mm GMT. Pricing at Microsoft retail list." byline.
 *  en-GB + short time. The retail-list scope tells the reader the dollar figures
 *  are estimates against published list pricing, not their negotiated rate. */
function formatByline(now: Date): string {
  const fmt = new Intl.DateTimeFormat('en-GB', { timeStyle: 'short' })
  return `By Guardian. As of ${fmt.format(now)} GMT. Pricing at Microsoft retail list.`
}

/** Severity bucket — drives both the verdict table and the color treatment. */
function bucketSeverity(monthly: number | null | undefined): LicenseWasteSeverity {
  if (monthly == null) return 'unknown'
  if (monthly === 0) return 'clean'
  if (monthly < 100) return 'low'
  if (monthly < 1000) return 'moderate'
  return 'large'
}

/**
 * Format a USD/month figure for the headline.
 *   - null/undefined → em-dash (visually matches "no data" cells)
 *   - sub-$1 → "$0.50/mo" (two-decimal so the value isn't lost in rounding)
 *   - $1 to $10 → "$3/mo" (whole dollars are cleaner; rounding loss is small)
 *   - >$10 → "$1,240/mo" (whole dollars, thousands-grouped)
 */
function formatUsdMonthly(amount: number | null | undefined): string {
  if (amount == null) return '—' // em-dash
  if (amount > 0 && amount < 1) {
    return `$${amount.toFixed(2)}/mo`
  }
  const rounded = Math.round(amount)
  return `$${rounded.toLocaleString('en-US')}/mo`
}

/** Annual call-out, e.g. "$14,880/yr at list price". */
function formatUsdAnnual(amount: number): string {
  const rounded = Math.round(amount)
  return `$${rounded.toLocaleString('en-US')}/yr at list price`
}

/** Format an idle-SKU's monthly waste for inline lead-sentence use:
 *  "≈$540/mo". Sub-$1 collapses to "≈$1/mo" so the lead sentence reads
 *  cleanly when a SKU happens to have trivial price data. */
function formatSkuMonthly(amount: number): string {
  const rounded = Math.max(1, Math.round(amount))
  return `≈$${rounded.toLocaleString('en-US')}/mo`
}

/** A seat count, thousands-grouped: "4,275". */
function formatCount(n: number): string {
  return n.toLocaleString('en-US')
}

/** "1 idle seat" / "3 idle seats". */
function idleSeatPhrase(n: number): string {
  return `${formatCount(n)} idle seat${n === 1 ? '' : 's'}`
}

/* -------------------------------------------------------------------------- */
/* Verdict — one line per severity                                            */
/* -------------------------------------------------------------------------- */

const VERDICT: Record<LicenseWasteSeverity, string> = {
  clean: 'Every paid seat is assigned.',
  low: 'Reclaim at the next license review.',
  moderate: 'Reclaim the idle seats this month.',
  large: 'Start with the largest idle SKU.',
  unknown: 'Pricing data still loading.',
}

/* -------------------------------------------------------------------------- */
/* Lead sentence — one line per data shape                                    */
/* -------------------------------------------------------------------------- */

const LEAD = {
  UNKNOWN: 'Pricing will appear after the next catalog refresh.',
  CLEAN_WITH_SKUS: 'Every paid seat is assigned across {n} SKU{skuPlural}.',
  CLEAN_NO_SKUS: 'Every paid seat is assigned.',
  LOW_ONE: 'One seat sits unassigned. Reclaim it at the next license review.',
  LOW_MANY: '{totalIdle} seats sit unassigned. Reclaim them at the next license review.',
  LOW_UNKNOWN: 'A small idle balance sits on the license list. Reclaim it at the next license review.',
  MODERATE_SKU: '{sku} carries the most idle spend, with {idleSeats} at {idleWaste}.',
  LARGE_SKU: '{sku} carries the most idle spend, with {idleSeats} at {idleWaste}. That is {annual}.',
  MODERATE_NO_SKU: '{totalIdle} unassigned seats are billing each month.',
  LARGE_NO_SKU: '{totalIdle} unassigned seats are billing each month. That is {annual}.',
}

function buildLeadSentence(
  severity: LicenseWasteSeverity,
  input: LicenseWasteBriefInput,
): string {
  const top = input.topIdleSkus?.[0]
  const totalIdle =
    typeof input.totalIdleSeats === 'number' && input.totalIdleSeats > 0
      ? input.totalIdleSeats
      : undefined

  if (severity === 'unknown') {
    return LEAD.UNKNOWN
  }

  if (severity === 'clean') {
    const skuCount = input.topIdleSkus?.length ?? 0
    if (skuCount > 0) {
      return LEAD.CLEAN_WITH_SKUS.replace('{n}', String(skuCount)).replace(
        '{skuPlural}',
        skuCount === 1 ? '' : 's',
      )
    }
    return LEAD.CLEAN_NO_SKUS
  }

  if (severity === 'low') {
    if (totalIdle === 1) return LEAD.LOW_ONE
    if (totalIdle != null) return LEAD.LOW_MANY.replace('{totalIdle}', formatCount(totalIdle))
    return LEAD.LOW_UNKNOWN
  }

  // moderate or large from here on.
  // Resolve the annual figure: prefer the explicit value, otherwise derive
  // *12 from the monthly. This is the only place we need it — the public
  // brief shape doesn't expose annual on its own.
  const monthly =
    typeof input.totalMonthlyWasteUsd === 'number' ? input.totalMonthlyWasteUsd : 0
  const annualValue =
    typeof input.totalAnnualWasteUsd === 'number'
      ? input.totalAnnualWasteUsd
      : monthly * 12
  const annualPhrase = formatUsdAnnual(annualValue)

  if (top) {
    const tpl = severity === 'large' ? LEAD.LARGE_SKU : LEAD.MODERATE_SKU
    return tpl
      .replace('{sku}', top.displayName)
      .replace('{idleSeats}', idleSeatPhrase(top.idleSeats))
      .replace('{idleWaste}', formatSkuMonthly(top.estimatedMonthlyWasteUsd))
      .replace('{annual}', annualPhrase)
  }

  // No SKU detail — fall back to a count-only sentence (still leads with
  // a named subject: the unassigned-seat count, not the row count).
  const tpl = severity === 'large' ? LEAD.LARGE_NO_SKU : LEAD.MODERATE_NO_SKU

  // If we don't even have an idle-seat count, soften "{totalIdle}" to a
  // plain word so the sentence still reads cleanly. (This is a rare path —
  // the server already returns totalIdleSeats whenever it returns waste $.)
  const display = totalIdle != null ? formatCount(totalIdle) : 'Several'
  return tpl.replace('{totalIdle}', display).replace('{annual}', annualPhrase)
}

/* -------------------------------------------------------------------------- */
/* Action-first headline + lede (blueprint §6.2 row 9)                        */
/* -------------------------------------------------------------------------- */

const HEADLINE_ACTION = {
  UNKNOWN: 'Check back after the next catalog refresh.',
  CLEAN: 'Nothing to review.',
  RECLAIM_ONE: 'Reclaim the unassigned seat at the next license review.',
  RECLAIM: 'Reclaim the {n} unassigned seats at the next license review.',
  RECLAIM_UNCOUNTED: 'Reclaim the unassigned seats at the next license review.',
}

const LEDE = {
  UNKNOWN: '**Guardian has no list pricing for these licenses yet**.',
  CLEAN: '**Every paid seat is assigned**.',
  SKU: '**{sku} carries the most idle spend**, with {idleSeats} at {idleWaste}.',
  SEATS_ONE: '**1 unassigned seat is billing each month**.',
  SEATS: '**{n} unassigned seats are billing each month**.',
  SEATS_UNCOUNTED: '**Unassigned seats are billing each month**.',
}

function buildActionFields(
  severity: LicenseWasteSeverity,
  input: LicenseWasteBriefInput,
): { headline: string; lede: string } {
  if (severity === 'unknown') return { headline: HEADLINE_ACTION.UNKNOWN, lede: LEDE.UNKNOWN }
  if (severity === 'clean') return { headline: HEADLINE_ACTION.CLEAN, lede: LEDE.CLEAN }

  const idle =
    typeof input.totalIdleSeats === 'number' && Number.isFinite(input.totalIdleSeats) && input.totalIdleSeats > 0
      ? input.totalIdleSeats
      : undefined
  const headline =
    idle === undefined
      ? HEADLINE_ACTION.RECLAIM_UNCOUNTED
      : idle === 1
        ? HEADLINE_ACTION.RECLAIM_ONE
        : HEADLINE_ACTION.RECLAIM.replace('{n}', formatCount(idle))

  const top = input.topIdleSkus?.[0]
  if (top) {
    const lede = LEDE.SKU.replace('{sku}', top.displayName)
      .replace('{idleSeats}', idleSeatPhrase(top.idleSeats))
      .replace('{idleWaste}', formatSkuMonthly(top.estimatedMonthlyWasteUsd))
    return { headline, lede }
  }
  const lede =
    idle === undefined ? LEDE.SEATS_UNCOUNTED : idle === 1 ? LEDE.SEATS_ONE : LEDE.SEATS.replace('{n}', formatCount(idle))
  return { headline, lede }
}

/* -------------------------------------------------------------------------- */
/* Public API                                                                 */
/* -------------------------------------------------------------------------- */

/**
 * Compose a LicenseWasteBrief from the active licensing posture. All inputs
 * are optional — the function degrades gracefully toward a plausible "no
 * data" brief rather than throwing.
 *
 * When `totalMonthlyWasteUsd` is null/undefined we surface the catalog-warming
 * copy and an em-dash headline rather than coercing to $0/mo, which would
 * land us in the "clean" band and tell the user something we don't actually
 * know.
 *
 * Annual figure: caller can pass it explicitly; otherwise we derive *12 from
 * the monthly figure. We never fabricate it from the SKU list.
 */
export function composeLicenseWasteBrief(
  input: LicenseWasteBriefInput = {},
): LicenseWasteBrief {
  const now = input.asOfDate ?? new Date()
  const severity = bucketSeverity(input.totalMonthlyWasteUsd)

  const verdict = VERDICT[severity]
  const headlineMonthly = formatUsdMonthly(input.totalMonthlyWasteUsd)
  const byline = formatByline(now)
  const leadSentence = buildLeadSentence(severity, input)
  const { headline, lede } = buildActionFields(severity, input)

  return {
    verdict,
    headlineMonthly,
    byline,
    leadSentence,
    severity,
    headline,
    lede,
  }
}

/* -------------------------------------------------------------------------- */
/* Test-only exports                                                          */
/* -------------------------------------------------------------------------- */

/** String tables exposed for shape-assertions in tests. Not part of the
 *  public runtime API; do not import from app code. */
export const __TEST_ONLY__ = {
  VERDICT,
  LEAD,
  HEADLINE_ACTION,
  LEDE,
  formatUsdMonthly,
  formatUsdAnnual,
  formatSkuMonthly,
  bucketSeverity,
}
